import { CostComparisonChart } from '@/components/charts';
import type { Slide } from '@/components/investor';

/**
 * DialogMe seed pitch. Mirrors docs/investors/pitch.md; every figure comes from
 * docs/business-plan/dialogme-business-plan.md — keep the three in sync.
 */
export const pitchSlides: Slide[] = [
  {
    nav: 'Cover',
    kicker: 'DialogMe · Seed pitch',
    title: 'The Business Becomes a Service Customers Can Talk To — and It Finishes the Job.',
    lead: 'DialogMe automates customer communication end to end. Not a chatbot that answers and hands off — a managed agentic platform that understands what the customer wants, does it in the business’s real systems, and closes with a booking, an order, a ticket or a payment.',
    blocks: [
      {
        kind: 'metrics',
        items: [
          { value: '6× – 11×', label: 'Cheaper per customer interaction than published incumbent rates' },
          { value: '83–87%', label: 'Gross margin on the platform — inference is owned, not rented' },
          { value: '2.04×', label: 'Throughput of the Fnk stack DialogMe runs on, over the assembled open-source stack' },
          { value: '0', label: 'Paying customers. Every commercial figure is a scenario', tone: 'muted' },
        ],
      },
      {
        kind: 'cards',
        items: [
          { title: 'Founder — Alisher Alimov', text: 'Systems engineering at NVIDIA. Previously co-founder and CTO of Gridfore. Sole author of the DialogMe platform and of the Fnk stack beneath it.' },
          { title: 'Base — Spain, selling into Europe', text: '3.3 million enterprises; AI adoption at **8.7%** among small firms against **49.2%** among large ones.' },
          { title: 'Stage — open beta, pre-revenue', text: 'Live Telegram, ticketing, booking, content and subscription integrations. Built on ≈€23,000, self-funded.' },
        ],
      },
    ],
  },
  {
    nav: 'Vision',
    kicker: 'Vision, mission and positioning',
    title: 'LLMs Are the New Interface to Business',
    blocks: [
      {
        kind: 'matrix',
        head: ['', 'Statement'],
        rows: [
          ['Vision', 'LLMs are the new interface to business — buttons, menus and forms give way to human-like conversation, in text or by voice'],
          ['Mission', 'Give every small business the customer conversation that only large companies could afford — answered in seconds, day or night, and finished'],
          ['Positioning', '*The receptionist that finishes the job* — for the customer. *Agentic customer communication at small-business prices, because the inference is owned* — for an investor'],
        ],
        accentRows: [0],
      },
      {
        kind: 'callout',
        title: 'Where DialogMe sits',
        text: 'Chatbots that small businesses can afford answer and hand off. Agents that act are priced per resolution for enterprises, on rented frontier models. DialogMe is the managed platform that finishes the job — integrations already built, inference owned — priced **per conversation, not per seat**, from €39 a month.',
      },
    ],
  },
  {
    nav: 'Problem',
    kicker: 'Problem',
    title: 'Chatbots Answer. They Don’t Act.',
    blocks: [
      {
        kind: 'cards',
        cols: 2,
        items: [
          { title: 'Customers stopped clicking', text: 'They want to say what they need — and increasingly they hand the task to an AI assistant.' },
          { title: 'Bots deflect, then hand off', text: 'The moment a customer wants to buy, book or pay, they are pushed to a human or another app — and many leave.' },
          { title: 'Revenue leaks after hours', text: 'Orders and bookings arrive 24/7; most small businesses respond only in working hours.' },
          { title: 'The good agents are priced for enterprises', text: 'Sierra, Decagon and Fin act — at $0.50–$2.00 per resolution or six-figure contracts, on rented models.' },
        ],
      },
      {
        kind: 'callout',
        title: 'The real blocker',
        text: '**It is not the agent — it is everything the agent has to reach.** The channel, the catalog, the calendar, the ticketing system and the payment provider: each a separate project, each maintained forever.',
      },
    ],
  },
  {
    nav: 'Solution',
    kicker: 'Solution',
    title: 'One Agentic Platform, Message to Transaction',
    blocks: [
      {
        kind: 'matrix',
        head: ['Outcome', 'What it means'],
        rows: [
          ['Answer', 'Grounded in the business’s own documents and catalog'],
          ['Show and recommend', 'Product grids rendered inside the conversation'],
          ['Book', 'Real availability, confirmed and written back to the calendar'],
          ['Take payment', 'Checkout in-chat, behind an explicit confirmation gate'],
          ['Escalate properly', 'Structured tickets with full context, clean human handover'],
        ],
        minWidth: '420px',
      },
      {
        kind: 'text',
        text: '**Five service types ship preconfigured** — E-Commerce, Offline Business, Blog, Portfolio and Finance — each with the **Fnk Harness** pack for it: workflows, tool schemas, policy packs, confirmation gates and evaluation sets, configured at onboarding rather than built.',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'What we are deliberately not',
        text: 'We do not train foundation models. We do not sell a toolkit to build on — we sell the service already configured. And we do not sell private enterprise deployments: a business that needs one gets our own stack as a [Fnk](https://fnk.ai) deployment.',
      },
    ],
  },
  {
    nav: 'How it works',
    kicker: 'How it works',
    title: 'Intent In, Transaction Out',
    blocks: [
      {
        kind: 'matrix',
        head: ['Step', 'What happens'],
        rows: [
          ['1 · Intent', 'The customer says what they want, on the channel they already use'],
          ['2 · Grounding', 'Retrieval over the business’s own content — posts, catalog, policies'],
          ['3 · Tool call', 'A call into the real system: calendar, catalog, ticketing, payment provider'],
          ['4 · Confirmation gate', 'Anything that costs money or commits the customer stops for explicit approval'],
          ['5 · Transaction', 'Written to the system of record, with a full evidence trail'],
        ],
        accentRows: [3],
        minWidth: '420px',
      },
      {
        kind: 'callout',
        title: 'Every service is also an MCP endpoint',
        text: 'External AI agents can discover what the business can do and transact with it directly. **The businesses reachable that way are the ones that get used.**',
      },
    ],
  },
  {
    nav: 'Integrations',
    kicker: 'Integrations',
    title: 'The Integration Surface Is the Product',
    blocks: [
      {
        kind: 'matrix',
        head: ['Layer', 'Integrations — bold is live today'],
        rows: [
          ['Channels', '**Telegram** · WhatsApp · Facebook · Instagram · Web widget'],
          ['Business systems', '**DialogMe ticketing** · **Cal.com** · **DialogMe content** · Jira · Zendesk · Calendly · Google Calendar · WordPress · Ghost'],
          ['Commerce and money', '**Ko-fi** · **Buy Me a Coffee** · Patreon · Shopify · Medusa · Saleor · Stripe · PayPal'],
          ['Inference', '**Fnk stack, operated by DialogMe** · approved partner endpoints on higher plans'],
        ],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Stated plainly',
        text: '**Shop and Payment close the revenue loop, and both are still in build.** In-chat checkout is the highest-value item on the roadmap, and this deck does not pretend it is done.',
      },
    ],
  },
  {
    nav: 'Why now',
    kicker: 'Why now',
    title: 'Three Shifts, All Moving in the Same Direction',
    blocks: [
      {
        kind: 'cards',
        items: [
          { title: 'The buyer is becoming a machine', text: 'MCP and agent-to-agent protocols let one system discover what another can do and transact with it directly.' },
          { title: 'Regulation became a feature', text: 'EU AI Act transparency obligations apply from **2 August 2026**. A service that produces its own disclosure and audit trail is a requirement.' },
          { title: 'The category is being bought', text: 'Salesforce paid **$3.6bn** for Fin. Sierra raised at **$15bn**. HubSpot halved its resolution price. Validated at scale — above our price point.' },
        ],
      },
      {
        kind: 'callout',
        text: 'The incumbents proved the demand and priced it for the enterprise. **Nobody has made it work at small-business economics, because nobody in that tier owns their inference.**',
      },
    ],
  },
  {
    nav: 'Built on Fnk',
    kicker: 'Technology',
    title: 'Owned Inference Is Why €39 Works',
    lead: 'DialogMe runs on its own AI stack, [Fnk](https://fnk.ai) — the Harness, the agent framework and the reactive inference engine beneath the service. One stack, no per-token supplier in between.',
    blocks: [
      {
        kind: 'matrix',
        head: ['At concurrency 64, 21 Aug 2026', 'Assembled stack', 'Fnk AI stack', 'Improvement'],
        rows: [
          ['Completed requests per second', '4.76', '**9.70**', '**2.04×**'],
          ['Weighted mean latency', '9,792 ms', '**6,225 ms**', '**−36.4%**'],
        ],
        numeric: [1, 2, 3],
      },
      {
        kind: 'text',
        text: 'Measured against Spring MVC + LangChain4j + vLLM, same model, same NVIDIA GPU. **For a subscription business the reading is simple: the same GPU serves about twice the conversations** — which is what lets €39 carry 250 of them at ≈81% margin.',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Two honest limits',
        text: 'One measured campaign, not yet independently reproduced — the method is at [fnk.ai](https://fnk.ai). And **a written, transferable licence for the Fnk stack is an open item**, to be signed before any external investor enters.',
      },
    ],
  },
  {
    nav: 'Economics',
    kicker: 'Economics',
    title: 'Priced Per Conversation, on Inference We Own',
    blocks: [
      {
        kind: 'matrix',
        head: ['Plan', 'Price / mo', 'Conversations', 'Rate', 'Margin'],
        rows: [
          ['Free', '€0', '50 in total', '—', '—'],
          ['Starter', '€39', '250', '€0.156', '≈81%'],
          ['Scale', '€299', '3,500', '€0.085', '≈79%'],
          ['Custom', 'Sales', 'Unlimited', 'Contracted', 'Deal'],
        ],
        accentRows: [1, 2],
        numeric: [1, 2, 3, 4],
      },
      { kind: 'node', node: <CostComparisonChart /> },
      {
        kind: 'cards',
        cols: 2,
        items: [
          { title: 'All of it recurs', text: 'Every euro is subscription revenue, from the first month.' },
          { title: 'The number to interrogate', text: 'Blended ARPU rising €45 → €60 is the mix moving onto Scale. **Starter-only cuts Year-5 revenue 35%** and takes cash below zero.' },
        ],
      },
    ],
  },
  {
    nav: 'Market',
    kicker: 'Market',
    title: 'The Base Case Needs One Twentieth of One Percent',
    blocks: [
      {
        kind: 'metrics',
        items: [
          { value: '≈130,000', label: 'Spanish ideal-customer profile, filtered from INE DIRCE', tone: 'plain' },
          { value: '≈1.1M', label: 'EU extension, same filters', tone: 'plain' },
          { value: '≈660', label: 'Year-5 base-case paying accounts' },
          { value: '≈0.05%', label: 'Share of the EU frame' },
        ],
      },
      {
        kind: 'text',
        text: 'Spain alone counts roughly 11,775 hair salons and 13,718 beauty salons; the base case’s Year-5 accounts are under 3% of that one vertical. **The gap between 8.7% and 49.2% adoption is the market.**',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'The honest limit',
        text: 'Every filter is a management assumption on an official frame. The constraint is not the market’s size; it is whether the funnel converts and retains.',
      },
    ],
  },
  {
    nav: 'Competition',
    kicker: 'Competition',
    title: 'Nobody Sells Exactly This — No Row Matches on All Five',
    blocks: [
      {
        kind: 'matrix',
        head: ['Company', 'Answers', 'Acts', 'Your customers', 'Priced for SMBs', 'Owns inference'],
        rows: [
          ['DialogMe', '●', '●', '●', '●', '● (Fnk)'],
          ['Sierra, Decagon, Fin', '●', '●', '●', '✖', '✖'],
          ['Tidio, ManyChat, Chatbase', '◐', '◐', '●', '●', '✖'],
          ['Botpress, Voiceflow', '●', '◐', '●', '◐', '✖'],
          ['Chatwoot (self-hosted)', '◐', '✖', '●', '●', '✖'],
        ],
        accentRows: [0],
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Where we should not compete',
        text: 'Businesses standardised on HubSpot, Zendesk or Salesforce; WhatsApp-only buyers until the channel ships; teams that want to build their own agent. **Our weakest published comparison is the entry tier** — Chatbase at $19 is cheaper than €39.',
      },
    ],
  },
  {
    nav: 'Strategy',
    kicker: 'Strategy and go-to-market',
    title: 'Small Business First, Because They Will Actually Try It',
    blocks: [
      {
        kind: 'matrix',
        head: ['Strategic choice', ''],
        rows: [
          ['Win one wedge before widening', 'Appointment businesses on Telegram, booking onto their own calendar; e-commerce once Shop and Payment ship'],
          ['Small business first', 'They decide in days, try the unproven, and produce the case study every later channel asks for'],
          ['Own the inference, price the conversation', 'Fixed-cost inference on the Fnk stack is what makes €39 structural'],
          ['Self-serve is the product', 'A question asked twice is a product defect'],
          ['Earn every step with paying accounts', 'No hire, channel spend or raise ahead of the evidence'],
        ],
      },
      {
        kind: 'matrix',
        head: ['Phase', 'What it is'],
        rows: [
          ['1 · Design partners and self-serve', 'Hand-onboarded partners in the appointment verticals on weekly loops; ten-minute self-serve onboarding'],
          ['2 · Concentrate', 'Associations, chambers and Kit Digital agents for the vertical that takes more than 40% of signups'],
          ['3 · Agency partners', 'Once a non-founder can onboard a business unassisted'],
        ],
      },
      {
        kind: 'matrix',
        head: ['Paying accounts', 'What it buys'],
        rows: [
          ['≈10 live', 'Self-serve works unassisted; the first retention signal'],
          ['≈100 paying', 'Two measured cohorts and a case study — **the company stops consuming founder capital**, and the seed conversation opens'],
          ['≈500–1,000', 'A reference list, vertical proof points, retention history a partner will accept'],
        ],
        accentRows: [1],
      },
      {
        kind: 'text',
        text: 'At €45–€60 blended ARPU, **roughly 110–140 paying accounts cover the entire development cost base.**',
      },
    ],
  },
  {
    nav: 'Team',
    kicker: 'Team',
    title: 'One Founder, Every Hire Gated on Revenue',
    blocks: [
      {
        kind: 'text',
        text: '**Alisher Alimov · Founder · 14+ years in distributed systems.** Senior systems engineering at NVIDIA. Previously co-founder and CTO of **Gridfore** — real-time analytics taken from R&D to a packaged enterprise platform, delivered to VEON and X5 Retail Group, presented at CES 2019 and MWC Shanghai; co-inventor of the registered Gridfore Reactive Intelligent Platform. Sole author of the DialogMe platform and the Fnk stack beneath it.',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'What a reader should hold against it',
        text: 'A single founder building both the platform and the stack beneath it is concentration risk. The cost base is kept at its floor — founder unpaid, no contracted capacity before revenue — and every hiring trigger is written down in advance. *Gridfore results are company-level. NVIDIA is not a sponsor, customer, investor or source of venture IP.*',
      },
    ],
  },
  {
    nav: 'The ask',
    kicker: 'The numbers and the ask',
    title: 'Self-Funded Through Development — and Thin in the Worst Case',
    blocks: [
      {
        kind: 'matrix',
        head: ['Scenario', 'Development', 'First revenue', 'Year-5 revenue', 'EBITDA-positive', 'Coverage'],
        rows: [
          ['Pessimistic', '24 months', 'Year 3 — €13,104', '€81,600', 'Not inside five years', '1.01×'],
          ['Base', '12 months', 'Year 2 — €39,420', '€409,680', 'Year 4', '1.29×'],
          ['Optimistic', '6 months', 'Year 1 — €38,880', '€1,367,616', 'Year 2', '2.10×'],
        ],
        accentRows: [1],
        numeric: [3, 5],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: 'Development costs **€22,076** in year one and **€17,976** a year thereafter, founder unpaid, funded by three **€50,000** founder rounds. **€0 of external funding is required in any scenario — only just in the pessimistic one**, which ends Year 5 with €1,515 left. The eighteen months between best and worst are worth **€1,286,016** of Year-5 revenue.',
      },
      {
        kind: 'metrics',
        items: [
          { value: '€0.8–1.5M', label: 'Seed modelled, base column — once ≈100 accounts pay' },
          { value: '€4–6M', label: 'Pre-money valuation', tone: 'plain' },
          { value: '15–22%', label: 'Implied dilution', tone: 'plain' },
          { value: '18–24 mo', label: 'Runway at the resulting cost base', tone: 'plain' },
        ],
      },
      {
        kind: 'callout',
        title: 'Not yet — and the exception, stated in advance',
        text: 'The recommendation of record is not to raise during development. Engineering 40% (Shop and Payment first), go-to-market 35%, support 10%, security, working capital and reserve 15%. **If the first market year closes under fifty paying accounts, a €200,000–€400,000 pre-seed becomes the prudent move.** Contact: [investors@dialogme.ai](mailto:investors@dialogme.ai).',
      },
    ],
  },
];
